// Types + metadata for the federated search endpoint (backoffice apps/search envelope).
import { VIA_SEARCH_PARAM } from 'uiComponents/searchScopeBadge'
import { LucideIconName } from 'utils/lucideIcon'

export type SearchTypeKey = 'order' | 'membership' | 'article' | 'products_list' | 'group_booking'

export interface SearchResult {
    type: SearchTypeKey
    id: string
    title: string
    subtitle: string
    deeplink: string
    score: number
    fields: Record<string, unknown>
}

export interface GroupResult {
    total: number
    results: SearchResult[]
    partial: boolean
    error?: string | null
}

export interface SearchResponse {
    groups: Partial<Record<SearchTypeKey, GroupResult>>
    // Per-type filter validation errors; a type with invalid filters is omitted from groups
    filterErrors?: Record<string, unknown>
}

export interface SearchRequest {
    q: string
    types?: SearchTypeKey[]
    filters?: Partial<Record<SearchTypeKey, Record<string, unknown>>>
    limit?: number
}

export interface SearchTypeMeta {
    key: SearchTypeKey
    label: string
    iconName: LucideIconName
    hint: string
    // List page accepting a `q` query param, used by the group's "View all" link
    viewAllPath?: (accountSlug: string, query: string) => string
}

// Mirrors the auto-detection the orders search bar applies to typed text
// (orders/orders/filters/searchAndDate.tsx); without a match the page falls
// back to its stored searchBy preference.
const ORDERS_SEARCH_BY_PATTERNS: [RegExp, string][] = [
    [/@/, 'email'],
    [/^\d{8,9}$/, 'id'],
    [/^\d{14}$/, 'barcode'],
    [/^\d{5}-\d{5}-\d{5}$/, 'reference_id'],
]

// The orders page reads `search`/`searchBy`, not `q`.
const ordersViewAllPath = (accountSlug: string, query: string): string => {
    const params = new URLSearchParams({ search: query })
    const searchBy = ORDERS_SEARCH_BY_PATTERNS.find(([pattern]) => pattern.test(query))?.[1]
    if (searchBy) {
        params.set('searchBy', searchBy)
    }
    params.set(VIA_SEARCH_PARAM, `Search "${query}"`)
    return `/account/${accountSlug}/orders/orders/?${params.toString()}`
}

const membershipsViewAllPath = (accountSlug: string, query: string): string => {
    const params = new URLSearchParams({ q: query, [VIA_SEARCH_PARAM]: `Search "${query}"` })
    return `/account/${accountSlug}/memberships/?${params.toString()}`
}

export const SEARCH_TYPE_META: SearchTypeMeta[] = [
    {
        key: 'order',
        label: 'Orders',
        iconName: 'ReceiptText',
        hint: 'Order number, reference, barcode or customer email',
        viewAllPath: ordersViewAllPath,
    },
    {
        key: 'membership',
        label: 'Memberships',
        iconName: 'IdCard',
        hint: 'Member name, email or barcode',
        viewAllPath: membershipsViewAllPath,
    },
    {
        key: 'article',
        label: 'Products',
        iconName: 'Ticket',
        hint: 'Product name',
        viewAllPath: (accountSlug, query) =>
            `/account/${accountSlug}/products/home/flat?q=${encodeURIComponent(query)}`,
    },
    { key: 'products_list', label: 'Categories', iconName: 'Layers', hint: 'Category name' },
    { key: 'group_booking', label: 'Group bookings', iconName: 'Users', hint: 'Booking reference or contact details' },
]

// Terms under 2 chars are ignored by the endpoint (min-query guard) — don't call.
export const MIN_QUERY_LENGTH = 2

export type GlobalSearchErrorCode = 'validation_error' | 'no_access' | 'account_not_found' | 'unknown'

export class GlobalSearchError extends Error {
    constructor(message: string, public code: GlobalSearchErrorCode, public filterErrors?: Record<string, unknown>) {
        super(message)
        this.name = 'GlobalSearchError'
    }
}

// Reservation references look like 17846-98922-06637.
export const RESERVATION_REFERENCE_PATTERN = /\b\d{4,6}(?:-\d{4,6}){2}\b/

// Reservations have no detail URL; land on the pending reservations list
// filtered down to one reference. Pin every status and a lifetime range, or
// the page's defaults (open-ish, last 30 days) would hide completed/expired/
// old reservations.
export const pendingReservationsSearchPath = (accountSlug: string, reference: string, viaSearch: string): string => {
    const params = new URLSearchParams({
        q: reference,
        dateRange: 'lifetime',
        filter: 'reservation_status:open,pending,initiated,overdue,expired,cancelled,completed',
        [VIA_SEARCH_PARAM]: viaSearch,
    })
    return `/account/${accountSlug}/orders/pending-reservations?${params.toString()}`
}

// Backend product subtitles carry the internal name, which usually mirrors
// the display name. The product lists (categories) in `fields` tell the user
// where the product lives outside search, so those win when present.
export const resultSubtitle = (result: SearchResult): string => {
    if (result.type !== 'article') {
        return result.subtitle
    }
    const lists = result.fields?.products_lists
    const names = (Array.isArray(lists) ? lists : [])
        .map((list) => (list && typeof list === 'object' ? (list as { name?: unknown }).name : undefined))
        .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
    return names.length ? names.join(' · ') : result.subtitle
}

// The backend membership deeplink is a plain path, but membership details only open
// via the hash route on the memberships page (see memberships/index.tsx).
export const resolveDeeplink = (result: SearchResult, accountSlug: string): string => {
    if (result.type === 'membership') {
        return `/account/${accountSlug}/memberships#membership_details?membershipUuid=${result.id}`
    }
    if (result.type === 'group_booking') {
        // The search envelope names this field `reference_id`.
        const reference =
            (typeof result.fields?.reference_id === 'string' && result.fields.reference_id) ||
            (typeof result.fields?.reference === 'string' && result.fields.reference) ||
            result.title.match(RESERVATION_REFERENCE_PATTERN)?.[0] ||
            result.id
        return pendingReservationsSearchPath(accountSlug, reference, `Group booking ${reference}`)
    }
    return result.deeplink
}
